import React, { useEffect, useRef, useState } from "react";
import { Carousel, FloatButton, Select } from "antd";
import { DDRDisplayData } from "../../../types/DDRDisplayData";
import "./SubTableManager.css";
import { subTableOrder, subTables } from "../../../utils/Enum";
import { fetchAllSubTableData, getFilteredTables } from "../../../backendInteraction/SubtableApiFetch";
import PersonnelTable from "../SubTables/PersonnelTable/PersonnelTable";
import { PlusOutlined } from "@ant-design/icons";
import ActivitiesTable from "../SubTables/ActivitiesTable/ActivitiesTable";
import DrillingTable from "../SubTables/DrillingTable/DrillingTable";
import CasingTable from "../SubTables/CasingTable/CasingTable";
import ConsumablesTable from "../SubTables/ConsumablesTable/ConsumablesTable";
import BitsTable from "../SubTables/BitsTable/BitsTable";
import DHSurveyTable from "../SubTables/DHSurveyTable/DHSurveyTable";
import GroutingTable from "../SubTables/GroutingTable/GroutingTable";
import RigMovesTable from "../SubTables/RigMovesTable/RigMovesTable";
import RigServiceTable from "../SubTables/RigServiceTable/RigServiceTable";
import PackingTable from "../SubTables/PackingTable/PackingTable";
import WaterTable from "../SubTables/WaterTable/WaterTable";
import FuelTable from "../SubTables/FuelTable/FuelTable";
import PowerPackTable from "../SubTables/PowerPackTable/PowerPackTable";
import SafetyDataTable from "../SubTables/SafetyDataTable/SafetyDataTable";
import TrainingTable from "../SubTables/TrainingTable/TrainingTable";
import {getIsDayShift} from "../../../utils/Validator";

const { Option } = Select;

interface SubTableManagerProps {
    ddr: DDRDisplayData | null;
    contractNo: string;
    shift: string;
    isView: boolean;
    onTotalHoursUpdate?: (total: number) => void;
    onTotalDrillingHoursUpdate?: (total: number) => void;
    onTotalDrillingMetresUpdate?: (total: number) => void;
    onTotalCoreLostUpdate?: (total: number) => void;
    onTotalPersonnelUpdate?: (total: number) => void;
}

const SubTableManager: React.FC<SubTableManagerProps> = ({ ddr, contractNo, shift, isView,
                                                             onTotalHoursUpdate, onTotalDrillingHoursUpdate,
                                                             onTotalDrillingMetresUpdate, onTotalCoreLostUpdate,
                                                             onTotalPersonnelUpdate }) => {
    const carouselRef = useRef<any>(null);
    const [tableData, setTableData] = useState<{ [key: string]: any[] }>({});
    const [filteredTables, setFilteredTables] = useState<number[]>([]);
    const [selectedTable, setSelectedTable] = useState<string>("personnel");
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [isAddNewRow, setIsAddNewRow] = useState<boolean>(false);
    const [holeId, setHoleIdState] = useState<string>(() => localStorage.getItem("DORS_HOLE_ID") || '');
    const [depth, setDepthState] = useState('');

    const setHoleId = (value: string) => {
        setHoleIdState(value);
        localStorage.setItem("DORS_HOLE_ID", value);
    };

    const setDepth = async (value: string) => {
        setDepthState(value);
        localStorage.setItem('DORS_DEPTH', value);
    };

    const tableComponents: {
        [key: string]: React.FC<{
            data: any[],
            plodId: number,
            contractNo: string,
            isDayShift: boolean,
            isView: boolean,
            isAddNewRow: boolean | null,
            setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null,
            holeId: string,
            depth: string,
            setHoleId: (value: string) => void,
            setDepth: (value: string) => void,
            onTotalHoursUpdate?: (total: number) => void;
            onTotalDrillingHoursUpdate?: (total: number) => void;
            onTotalDrillingMetresUpdate?: (total: number) => void;
            onTotalCoreLostUpdate?: (total: number) => void;
            onTotalPersonnelUpdate?: (total: number) => void;
        }>
    } = {
        personnel: PersonnelTable,
        activities: ActivitiesTable,
        drilling: DrillingTable,
        /*breakdowns: BreakdownTable,*/
        consumables: ConsumablesTable,
        casing: CasingTable,
        bits_and_reamers: BitsTable,
        dhsurvey: DHSurveyTable,
        grouting: GroutingTable,
        rig_moves: RigMovesTable,
        rig_service: RigServiceTable,
        packing: PackingTable,
        water: WaterTable,
        fuel: FuelTable,
        power_pack: PowerPackTable,
        safety_data: SafetyDataTable,
        training: TrainingTable,
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const storedUser = localStorage.getItem("DORS_USER");
                const aid = storedUser && JSON.parse(storedUser).Aid;
                const data = await fetchAllSubTableData(ddr?.pid ?? -1, aid, getIsDayShift(shift));
                setTableData(data);
            } catch (error) {
                console.error("Error fetching sub-tables data:", error);
            }
        };

        fetchData().catch(error => console.error("Error in fetchData:", error));
    }, [ddr]);

    useEffect(() => {
        if (tableData) {
            const nonEmptyTables = getFilteredTables(tableData);
            setFilteredTables(nonEmptyTables);

            if (nonEmptyTables.length > 0) {
                setSelectedTable(subTables[nonEmptyTables[0]].value);
                setCurrentSlide(nonEmptyTables[0]);
                carouselRef.current.goTo(nonEmptyTables[0]);
            }
        }
    }, [tableData]);

    const findNextPage = (goNext: boolean, value: number): number => {
        if (filteredTables.length === 0) return 0;
        if (filteredTables.length === 1) return filteredTables[0];

        if (goNext) {
            return filteredTables.find(num => num > value) ?? filteredTables[0];
        } else {
            const reversed = [...filteredTables].reverse();
            return reversed.find(num => num < value) ?? filteredTables[filteredTables.length - 1];
        }
    };

    const handleSubTableChange = (value: string) => {
        setSelectedTable(value);
        const index = subTableOrder[value];

        if (carouselRef.current && index !== undefined) {
            carouselRef.current.goTo(index);
            setCurrentSlide(index);
        }
    };

    const handleBeforeChange = (_: number, next: number) => {
        // Reset the add row state when switching tables
        setIsAddNewRow(false);

        if (selectedTable !== subTables[next].value) {
            let targetPage = next;

            if (!filteredTables.includes(next)) {
                if (next === 0 && currentSlide === 16) {
                    targetPage = findNextPage(true, next);
                } else if (next === 16 && currentSlide === 0) {
                    targetPage = findNextPage(false, next);
                } else {
                    targetPage = findNextPage(next > currentSlide, next);
                }
                carouselRef.current.goTo(targetPage);
            }
            setSelectedTable(subTables[targetPage].value);
            setCurrentSlide(targetPage);
        }
    };

    return (
        <div className="sub_table_container">
            <div className="sub_table_selector">
                <label>View sub-forms:</label>
                <Select className="antd_selector" value={selectedTable} onChange={handleSubTableChange}>
                    {subTables.map((table) => (
                        <Option key={table.value} value={table.value}>
                            {table.label}
                        </Option>
                    ))}
                </Select>
            </div>

            <div className="carousel_wrapper">
                <Carousel
                    ref={carouselRef}
                    arrows
                    infinite
                    draggable
                    beforeChange={handleBeforeChange}
                >
                    {Object.keys(subTableOrder).map((table) => {
                        const TableComponent = tableComponents[table];
                        return (
                            <div key={table} className="sub_tables">
                                {TableComponent ? (
                                    <TableComponent
                                        data={tableData[table] || []}
                                        plodId={ddr?.pid ?? -1}
                                        contractNo={contractNo}
                                        isDayShift={getIsDayShift(shift)}
                                        isView={isView}
                                        isAddNewRow={table === selectedTable ? isAddNewRow : null}
                                        setIsAddNewRow={isAddNewRow === true ? setIsAddNewRow : null}
                                        holeId={holeId}
                                        depth={depth}
                                        setHoleId={setHoleId}
                                        setDepth={setDepth}
                                        onTotalHoursUpdate={table === "activities" ? onTotalHoursUpdate : undefined}
                                        onTotalDrillingHoursUpdate={table === "activities" ? onTotalDrillingHoursUpdate : undefined}
                                        onTotalDrillingMetresUpdate={table === "drilling" ? onTotalDrillingMetresUpdate : undefined}
                                        onTotalCoreLostUpdate={table === "drilling" ? onTotalCoreLostUpdate : undefined}
                                        onTotalPersonnelUpdate={table === "personnel" ? onTotalPersonnelUpdate : undefined}
                                    />
                                ) : (
                                    <h3 className="contentStyle">{`${table} Table Placeholder`}</h3>
                                )}
                            </div>
                        );
                    })}
                </Carousel>

                {!isView && (
                    <FloatButton
                        icon={<PlusOutlined />}
                        tooltip="Add New Row"
                        onClick={() => {
                            if (!isAddNewRow) {
                                setIsAddNewRow(true);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SubTableManager;
